import ImageParams from "@/model/ImageParams";
import saveMetadata from "@/service/upload/saveMetadata";
import Photo from "@/model/Photo";
import Placeholder, { FinishedPlaceholder } from "@/model/Placeholder";
import Session from "@/model/Session";
import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { getModule } from "vuex-module-decorators";
import UserModule from "@/store/modules/User";
import PhotosModule from "@/store/modules/Photos";
import { convertRawToJpeg, isRawFile } from '@/service/rawConverter';
import { confirmRawConversion, RawConversionDecision } from '@/service/rawConversionConfirmation';
import sessionGuard from '@/service/sessionGuard';

interface Progress {
    current: number;
    overall: number;
}

const { $vToastify: notifier } = new Vue() as any;

export type Conflict = {
    conflictId: string,
    conflictType: 'FILENAME'|'HASH',
    photo: {
        filename: string;
        file: File;
    },
    smallThumb: string,
    largeThumb: string,
    sessionId: string,
    conflicted: Photo,
    resolve: (id?: string) => void
};

export type UploadStatus = 'waiting'|'progress'|'finished';
export type ExtendedUploadStatus = UploadStatus|'error';

export interface UploadState {
    sessionId: string;
    session: Session;
    status: UploadStatus;
    id?: string;
    file: File;
    progress: Progress;
    conflicted: boolean;
    overwriteId?: string;
}

type RequiredPlaceholderProps = ['id', 'filename', 'largeThumbUrl', 'smallThumbUrl', 'dimensions', 'smallThumbDimensions', 'largeThumbDimensions', 'dataHash', 'perceptualHash'];
const metadataRetries = new WeakMap<File, { sessionId: string; smallThumbUrl: string; largeThumbUrl: string; placeholder: Placeholder }>();
const metadataControllers = new WeakMap<File, AbortController>();

const placeholderKeys: RequiredPlaceholderProps = ['id', 'filename', 'largeThumbUrl', 'smallThumbUrl', 'dimensions', 'smallThumbDimensions', 'largeThumbDimensions', 'dataHash', 'perceptualHash'];

@Module({
    name: 'upload',
})
export default class UploadModule extends VuexModule {
    public placeholders: Record<string, Array<Placeholder>> = {};
    public rawPreparationCount = 0;

    // contains all photos that are currently being uploaded or recently finished (cleared automatically)
    public photoQueue: Array<UploadState> = [];
    public conflicts: Array<Conflict> = [];

    // contains all states from photoQueue but not being cleared automatically
    public uploadList: Array<Omit<UploadState, 'status'> & { status: ExtendedUploadStatus }> = [];

    get hasUploadErrors() {
        return this.uploadList.some(({ status }) => status === 'error');
    }

    @Mutation
    changeRawPreparationCount(delta: number) {
        this.rawPreparationCount = Math.max(0, this.rawPreparationCount + delta);
    }

    @Mutation
    addPlaceholder({ sessionId, placeholder }: { sessionId: string, placeholder: Placeholder }) {
        this.placeholders = {
            ...this.placeholders,
            [sessionId]: [...(this.placeholders[sessionId] || []), placeholder]
        };
    }

    @Mutation
    updatePlaceholder({ sessionId, file, placeholder }: { sessionId: string, file: File, placeholder: Partial<Placeholder> }) {
        const existingPlaceholder = (this.placeholders[sessionId] || []).find(
            ({ file: exFile }) => exFile === file
        );

        if (!existingPlaceholder) {
            for (const url of [placeholder.smallThumbUrl, placeholder.largeThumbUrl]) {
                if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
            }
            return;
        }
        for (const key of ['smallThumbUrl', 'largeThumbUrl'] as const) {
            const oldUrl = existingPlaceholder[key];
            if (placeholder[key] && oldUrl?.startsWith('blob:') && oldUrl !== placeholder[key]) URL.revokeObjectURL(oldUrl);
        }

        this.placeholders = {
            ...this.placeholders,
            [sessionId]: [
                ...this.placeholders[sessionId].filter(
                    (p) => p !== existingPlaceholder
                ),
                { ...existingPlaceholder, ...placeholder } as Placeholder
            ]
        }
    }

    @Mutation
    removePlaceholder({ file }: { file: File }) {
        const sessionId = (Object.entries(this.placeholders).find(
            ([_, placeholders]) => placeholders.some(({ file: pFile }) => pFile === file)
        ) || [])[0];

        if (sessionId) {
            const placeholder = this.placeholders[sessionId].find(item => item.file === file)!;
            for (const url of [placeholder.smallThumbUrl, placeholder.largeThumbUrl]) {
                if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
            }
            this.placeholders = {
                ...this.placeholders,
                [sessionId]: this.placeholders[sessionId].filter(
                    ({ file: exFile }) => exFile !== file
                )
            };
        }
    }

    @Mutation
    addConflict(conflict: Conflict) {
        this.conflicts = [...this.conflicts, conflict];
    }

    @Mutation
    removeConflict(conflict: Conflict) {
        const original = this.conflicts.find(item => item.conflictId === conflict.conflictId);
        for (const url of [conflict.smallThumb, conflict.largeThumb, original?.smallThumb, original?.largeThumb]) {
            if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
        }
        this.conflicts = this.conflicts.filter(c => c.conflictId !== conflict.conflictId);
    }

    @Mutation
    clearQueueIfFinished() {
        if (this.isFinished) {
            this.photoQueue = [];
        }
    }

    private get isFinished() {
        return this.photoQueue.every(({ status }) => status === 'finished');
    }

    @Mutation
    addUploadStates(states: Array<UploadState>) {
        // New upload batch should always start from a clean queue if everything visible there is already finished.
        if (this.photoQueue.length > 0 && this.photoQueue.every(({ status }) => status === 'finished')) {
            this.photoQueue = [];
        }
        this.photoQueue = [
            ...this.photoQueue,
            ...states,
        ];
        this.uploadList = [
            ...this.uploadList,
            ...states,
        ];
    }

    @Mutation
    addUploadErrors(states: Array<Omit<UploadState, 'status'>>) {
        this.uploadList = [
            ...this.uploadList,
            ...states.map((state) => ({
                ...state,
                status: 'error' as const,
            })),
        ];
    }

    @Mutation
    removeState({ file }: { file: File }) {
        this.photoQueue = this.photoQueue.filter(({ file: qFile }) => qFile !== file);
        this.uploadList = this.uploadList.filter(({ file: qFile }) => qFile !== file);
    }

    @Mutation
    markError({ file, error }: { file: File; error: unknown }) {
        this.photoQueue = this.photoQueue.filter(({ file: qFile }) => qFile !== file);
        const listElement = this.uploadList.find(({ file: qFile }) => qFile === file);
        if (listElement) {
            this.uploadList = this.uploadList.filter((candidate) => candidate !== listElement);
            this.uploadList = [
                ...this.uploadList,
                {
                    ...listElement,
                    status: 'error',
                }
            ];
        }
    }

    @Mutation
    updatePhotoQueueId({ file, id }: { file: File; id: string; }) {
        const state = this.photoQueue.find(({ file: qFile }) => file === qFile);
        if (!state) throw new Error(`Trying to set id on non-existent upload state for ${file.name}`);

        const replaceElement = <T extends Pick<UploadState, 'file'>>(list: Array<T>) => ([
            ...list.filter(({ file: qFile }) => file !== qFile),
            {
                ...state,
                id,
            }
        ]);

        this.photoQueue = replaceElement(this.photoQueue);
        this.uploadList = replaceElement(this.uploadList);
    }

    @Mutation
    clearFinishedUploadListElements() {
        this.uploadList = this.uploadList.filter(({ status }) => status !== 'finished' && status !== 'error');
    }

    @Mutation
    updateStatus({ photoId, status }: { photoId: string; status: 'progress'|'finished'; }) {
        for (const list of [this.photoQueue, this.uploadList]) {
            const state = list.find(item => item.id === photoId);
            if (state) {
                state.status = status;
                if (status === 'finished') state.progress.current = state.progress.overall;
            }
        }
    }

    @Mutation
    releaseFinishedFile({ file }: { file: File }) {
        // History only displays the filename; retry needs bytes only for failed uploads.
        const metadata = new File([], file.name, { type: file.type, lastModified: file.lastModified });
        for (const list of [this.photoQueue, this.uploadList]) {
            for (const state of list) if (state.file === file && state.status === 'finished') state.file = metadata;
        }
    }

    @Mutation
    updateProgress({ photoId, current, overall }: { photoId: string; current: number; overall: number }) {
        for (const list of [this.photoQueue, this.uploadList]) {
            const state = list.find(item => item.id === photoId);
            if (!state || state.status === 'finished' || overall <= 0) continue;
            state.progress.current = Math.min(state.progress.overall * 0.99, Math.max(0, current / overall * state.progress.overall));
        }
    }

    @Action({ rawError: true })
    setPlaceholderParams(payload: { file: File, sessionId: string, params: ImageParams }): void {
        const { params: { 
            imageSize: dimensions,
            smallThumbSize: smallThumbDimensions,
            largeThumbSize: largeThumbDimensions,
            ...params
        } } = payload;
        this.updatePlaceholder({
            sessionId: payload.sessionId,
            file: payload.file,
            placeholder: { ...params, dimensions, smallThumbDimensions, largeThumbDimensions },
        })
    }
    
    @Action({ rawError: true })
    public setPlaceholderSmallThumb(
        { sessionId, smallThumb, file }: { sessionId: string, smallThumb: Blob, file: File },
    ) {
        this.updatePlaceholder({
            sessionId,
            file,
            placeholder: { smallThumbUrl: URL.createObjectURL(smallThumb) },
        });
    }

    @Action({ rawError: true })
    public setPlaceholderLargeThumb(
        { sessionId, largeThumb, file }: { sessionId: string, largeThumb: Blob, file: File },
    ) {
        this.updatePlaceholder({
            sessionId,
            file,
            placeholder: { largeThumbUrl: URL.createObjectURL(largeThumb) },
        });
    }

    @Action({ rawError: true })
    public setPlaceholderHashes(
        { sessionId, file, dataHash, perceptualHash }: { sessionId: string; file: File; dataHash: string; perceptualHash: string; },
    ) {
        this.updatePlaceholder({
            sessionId,
            file,
            placeholder: {
                dataHash,
                perceptualHash,
            }
        });
    }

    @Action({ rawError: true })
    public setPlaceholderId({ sessionId, file, id }: { sessionId: string; file: File; id: string; }) {
        this.updatePlaceholder({
            sessionId,
            file,
            placeholder: {
                id,
            }
        });
        this.updatePhotoQueueId({ file, id });
    }

    @Action({ rawError: true })
    public markOverwrite({ sessionId, file, overwriteId }: { sessionId: string; file: File; overwriteId: string; }) {
        this.updatePlaceholder({
            sessionId,
            file,
            placeholder: {
                overwriteId,
            }
        })
        this.setOverwriteId({ file, overwriteId });
    }

    @Mutation
    setOverwriteId({ file, overwriteId }: { file: File; overwriteId: string }) {
        for (const list of [this.photoQueue, this.uploadList]) {
            const state = list.find(item => item.file === file);
            if (state) state.overwriteId = overwriteId;
        }
    }

    @Action({ rawError: true })
    public async cancelUpload({ file }: { file: File }) {
        metadataControllers.get(file)?.abort();
        metadataRetries.delete(file);
        (await import('@/service/upload')).default.cancel(file);
        this.conflicts.filter(conflict => conflict.photo.file === file).forEach(conflict => this.removeConflict(conflict));
        this.removePlaceholder({ file });
        this.removeState({ file });
    }

    @Action({ rawError: true })
    public cancelAll() {
        this.photoQueue.filter(({ status }) => status !== 'finished')
            .forEach(({ file }) => this.cancelUpload({ file }));
    }

    @Action({ rawError: true })
    public async uploadFiles({
        session,
        files,
        ids = new Map(),
        rawConversionDecision,
    }: {
        session: Session,
        files: File[],
        ids?: Map<File, string>,
        rawConversionDecision?: RawConversionDecision,
    }) {
        const rawFiles = files.filter(isRawFile);
        let preparedFiles = files;
        let preparedIds = ids;

        if (rawFiles.length > 0) {
            const decision = rawConversionDecision || await confirmRawConversion(files);
            if (!decision.confirmed) {
                return;
            }

            this.changeRawPreparationCount(1);
            const convertedFiles = new Map<File, File>();
            const conversionErrors: File[] = [];

            try {
                for (const file of rawFiles) {
                    try {
                        convertedFiles.set(file, await convertRawToJpeg(
                            file,
                            decision.settingsByFile?.get(file),
                            decision.advanced,
                        ));
                    } catch (error) {
                        console.error(`RAW conversion failed for ${file.name}`, error);
                        conversionErrors.push(file);
                    }
                }

                if (conversionErrors.length > 0) {
                    this.addUploadErrors(conversionErrors.map((file) => ({
                        file,
                        sessionId: session.id,
                        conflicted: false,
                        progress: {
                            current: 0,
                            overall: file.size,
                        },
                        session,
                    })));
                    notifier.error({
                        title: 'Błąd konwersji RAW',
                        body: `Nie udało się przekonwertować: ${conversionErrors.map(({ name }) => name).join('<br>')}`,
                    });
                }

                preparedFiles = files
                    .filter((file) => !isRawFile(file) || convertedFiles.has(file))
                    .map((file) => convertedFiles.get(file) || file);
                preparedIds = new Map<File, string>();
                ids.forEach((id, file) => {
                    const preparedFile = convertedFiles.get(file) || file;
                    if (!isRawFile(file) || convertedFiles.has(file)) {
                        preparedIds.set(preparedFile, id);
                    }
                });
            } finally {
                this.changeRawPreparationCount(-1);
                sessionGuard.touch();
            }
        }

        const [acceptedFiles, rejectedFiles] = preparedFiles.reduce(([pass, fail], file) => {
            if (['image/jpeg', 'image/png', 'image/tiff'].includes(file.type) ||
                (
                    file.type === '' &&
                    (
                        file.name.toLowerCase().endsWith('.png') ||
                        file.name.toLowerCase().endsWith('.jpg') ||
                        file.name.toLowerCase().endsWith('.jpeg') ||
                        file.name.toLowerCase().endsWith('.tiff')
                    )
                )) {
                pass.push(file);
            } else {
                fail.push(file);
            }
            return [pass, fail];
        }, [[], []] as [File[], File[]]);

        if (rejectedFiles.length > 0) {
            notifier.error({
                title: 'Zignorowano pliki',
                body: `Niektóre pliki nie są obrazami: ${rejectedFiles.map(({ name }) => name).join('<br>')}`,
            });
        }
        if (acceptedFiles.length === 0) {
            return;
        }

        // Check if uploading these files would exceed the storage limit
        const userModule = getModule(UserModule, (await import('@/store')).default);
        const totalUploadSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
        const availableStorage = userModule.user?.storageLimit ? userModule.user.storageLimit - userModule.usedStorageBytes : 0;

        if (availableStorage <= 0 || totalUploadSize > availableStorage) {
            notifier.error({
                title: 'Przekroczono limit miejsca',
                body: `Wykorzystałeś całą przestrzeń. Usuń coś, aby dodać nowe zdjęcia.`,
            });
            return;
        }

        this.addUploadStates(acceptedFiles.map((file) => ({
            file,
            sessionId: session.id,
            overwriteId: preparedIds.get(file),
            conflicted: false,
            status: 'waiting',
            progress: {
                current: 0,
                overall: file.size,
            },
            session,
        })));
        acceptedFiles.forEach((file) => this.addPlaceholder({
            sessionId: session.id,
            placeholder: {
                file,
                filename: file.name,
            }
        }));
        (await import('@/service/upload')).default.uploadFiles(
            session,
            acceptedFiles.map(file => ({ file, ...(preparedIds.has(file) && { id: preparedIds.get(file) }) })),
        );
    }

    @Action({ rawError: true })
    public async overwritePhoto({ id, session, file }: { id: string; session: Session; file: File; }) {
        const photosModule = getModule(PhotosModule, (await import('@/store')).default);
        const photo = photosModule.remotePhotos[id];
        if (photo && (photo.orderedBy || []).length > 0 && !session.finalizationMode) {
            notifier.error({
                title: 'Nie można podmienić.',
                body: 'Zdjęcie zostało kupione.',
            });
            return;
        }

        const ids = new Map<File, string>();
        ids.set(file, id);
        return this.uploadFiles({ session, files: [ file ], ids });
    }

    @Action({ rawError: true })
    public async uploadFinished(
        { sessionId, file, smallThumbUrl, largeThumbUrl, signal, reconcile = false }: { sessionId: string; file: File; smallThumbUrl: string; largeThumbUrl: string; signal?: AbortSignal; reconcile?: boolean; }
    ) {
        const placeholder = this.placeholders[sessionId].find(({ file: placeholderFile }) => placeholderFile === file);
        if (!placeholder) throw new Error('Trying to mark finished upload on non-existent placeholder');
        
        const finishedPlaceholder: FinishedPlaceholder = placeholderKeys.reduce((finished, key) => {
            if (!placeholder[key]) throw new Error(`Trying to mark finished upload on placeholder with missing ${key}`);

            return {...finished, [key]: placeholder[key]};
        }, {}) as FinishedPlaceholder;

        const photo: Photo = {
            ...finishedPlaceholder,
            smallThumbUrl,
            largeThumbUrl,
            recommended: false,
            finalized: false,
            likedBy: [],
            orderedBy: [],
        };
        
        const photoData = {
            checksum: photo.dataHash!, contentType: file.type, dimensions: photo.dimensions,
            filename: file.name, largeThumbnailDimensions: photo.largeThumbDimensions,
            perceptualHash: photo.perceptualHash!, smallThumbnailDimensions: photo.smallThumbDimensions,
        };
        if (signal?.aborted) return;
        try {
            await saveMetadata(photo.id, sessionId, photoData, !!placeholder.overwriteId, reconcile);
        } catch (error) {
            if (!signal?.aborted) metadataRetries.set(file, {
                sessionId, smallThumbUrl, largeThumbUrl,
                placeholder: { ...placeholder, smallThumbUrl, largeThumbUrl },
            });
            throw error;
        }
        if (signal?.aborted) return;
        metadataRetries.delete(file);
        if (placeholder.overwriteId) {
            this.context.commit('updateRemotePhoto', {
                photoId: placeholder.overwriteId,
                params: { ...finishedPlaceholder, smallThumbUrl, largeThumbUrl, finalized: false },
            });
            this.context.dispatch('refreshPhoto', photo.id);
        } else {
            this.context.commit('addRemoteSessionPhotos', { sessionId, photos: [photo] });
        }
        this.removePlaceholder({ file });
        this.updateStatus({ photoId: photo.id, status: 'finished' });
        this.releaseFinishedFile({ file });
        this.clearQueueIfFinished();
        if (this.isFinished) {
            setTimeout(() => this.context.dispatch('refreshUsedStorageBytes'), 10000);
        }
    }

    @Action({ rawError: true })
    public uploadError({ sessionId, file, error }: { sessionId: string; file: File; error: unknown; }) {
        const placeholder = this.placeholders[sessionId].find(({ file: placeholderFile }) => placeholderFile === file);
        if (!placeholder) return;

        this.removePlaceholder({ file });
        this.conflicts.filter(conflict => conflict.photo.file === file).forEach(conflict => this.removeConflict(conflict));
        this.markError({ file, error });
        notifier.error({
            title: 'Błąd przetwarzania obrazu',
            body: `Nie udało się przetworzyć obrazu ${file.name}`,
        });
    }

    @Action({ rawError: true })
    public async retryUpload({ file }: { file: File }) {
        const state = this.uploadList.find(({ file: qFile, status }) => qFile === file && status === 'error');
        if (!state) throw new Error(`Trying to retry upload on non-existent error state for ${file.name}`);
        
        this.removeState({ file });
        const metadata = metadataRetries.get(file);
        if (metadata) {
            const controller = new AbortController();
            metadataControllers.set(file, controller);
            this.addUploadStates([{ ...state, status: 'progress' }]);
            this.addPlaceholder({ sessionId: metadata.sessionId, placeholder: metadata.placeholder });
            try {
                await this.uploadFinished({ ...metadata, file, signal: controller.signal, reconcile: true });
            } catch (error) {
                if (!controller.signal.aborted) this.uploadError({ sessionId: metadata.sessionId, file, error });
            } finally {
                if (metadataControllers.get(file) === controller) metadataControllers.delete(file);
            }
            return;
        }
        return this.uploadFiles({
            session: state.session,
            files: [ file ],
            ...(state.overwriteId && { ids: new Map([[file, state.overwriteId]]) }),
        });
    }

    @Action({ rawError: true })
    public retryAllFailed() {
        return Promise.all(
            this.uploadList.filter(({ status }) => status === 'error')
                .map(({ file }) => this.retryUpload({ file }))
        );
    }

    get sessionProgress() {
        return Object.entries(
                this.photoQueue
                .reduce((perSession, { sessionId, progress }) => ({
                    ...perSession,
                    [sessionId]: [
                        ...(perSession[sessionId] || []),
                        progress,
                    ]
                }), {} as { [sessionId: string]: Array<Progress>})
            ).map(
                ([sessionId, progressArray]) => ([
                    sessionId,
                    progressArray.reduce((sum, photo) => ({
                        overall: sum.overall + photo.overall,
                        current: sum.current + photo.current,
                    }), { overall: 0, current: 0})
                ] as [string, Progress])
            ).map(([ sessionId, progress ]) => ([
                sessionId,
                progress.overall === 0 ? 0 : (progress.current * 100 / progress.overall)
            ] as [string, number])).reduce((all, [sessionId, progress]) => ({
                ...all,
                [sessionId]: progress,
            }), {} as { [sessionId: string]: number});
    }

    get sessionInProgressIds() {
        return Array.from(new Set(
            this.photoQueue
                .filter(({ status }) => status !== 'finished')
                .map(({ sessionId }) => sessionId)
        ));
    }    

    get statusByFile() {
        return this.photoQueue.reduce((map, { file, status }) => {
            map.set(file, status);
            return map;
        }, new Map<File, UploadStatus>());
    }

    get photoPercent() {
        return this.photoQueue.reduce((map, { file, progress: { current, overall } }) => {
            if (overall === 0) map.set(file, 0);
            else map.set(file, current / overall * 100);
            
            return map;
        }, new Map<File, number>());
    }

    get overallProgress() {
        const progressSum = this.photoQueue.reduce((prev, { progress: { current, overall } }) => ({
            current: prev.current + current,
            overall: prev.overall + overall
        }), { current: 0, overall: 0 });

        return progressSum.overall === 0 ? 0 : progressSum.current * 100 / progressSum.overall;
    }

    get uploadInProgress() {
        return this.rawPreparationCount > 0 ||
            this.photoQueue.some(({ status }) => status !== 'finished');
    }

    get waitingForUploadProgress() {
        return !this.photoQueue.some(({ status }) => status === 'progress') &&
            (this.rawPreparationCount > 0 || this.photoQueue.some(({ status }) => status === 'waiting'));
    }
}
